import type { ConsentClientConfig, StoredConsent } from "../types";
import { isGranted } from "./store";

/**
 * Vendor loaders and consent signals (docs/cookie-consent-plan.md §5). A vendor
 * script is injected only once its service is granted; Google Consent Mode,
 * Meta, Microsoft UET and Clarity additionally receive their own grant/revoke
 * signals so tags that are already running follow the visitor's choice.
 */

type W = Window & {
  fbq?: ((...args: unknown[]) => void) & { queue?: unknown[]; loaded?: boolean; version?: string; push?: unknown; callMethod?: (...args: unknown[]) => void };
  _fbq?: unknown;
  uetq?: unknown[] & { push: (...args: unknown[]) => void };
  UET?: new (options: Record<string, unknown>) => unknown;
  clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
  hj?: ((...args: unknown[]) => void) & { q?: unknown[] };
  _hjSettings?: { hjid: number; hjsv: number };
  _linkedin_partner_id?: string;
  _linkedin_data_partner_ids?: string[];
};

const loaded = new Set<string>();

function inject(src: string, attrs: Record<string, string> = {}, onload?: () => void): void {
  if (document.querySelector(`script[data-cms-vendor="${CSS.escape(src)}"]`)) return;
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  script.dataset.cmsVendor = src;
  // Injected by the runtime after a grant; the bootstrap guard must not intercept it.
  script.setAttribute("data-cms-allowed", "");
  for (const [key, value] of Object.entries(attrs)) script.setAttribute(key, value);
  if (onload) script.addEventListener("load", onload);
  document.head.appendChild(script);
}

function gtag(...args: unknown[]) {
  const w = window as W;
  w.dataLayer = w.dataLayer || [];
  if (!w.gtag) w.gtag = function gtagShim() { (w.dataLayer as unknown[]).push(arguments); };
  w.gtag(...args);
}

/** Google Consent Mode v2 state from the visitor's categories. security_storage is always granted. */
export function googleConsentState(config: ConsentClientConfig, stored: StoredConsent | null): Record<string, "granted" | "denied"> {
  const on = (serviceIds: string[], category: "functional" | "analytics" | "marketing") => {
    const relevant = serviceIds.filter((id) => config.categories.some((group) => group.id === category && group.services.some((service) => service.id === id)));
    if (relevant.length) return relevant.some((id) => isGranted(id, config, stored));
    const valid = stored && stored.rev === config.revision ? stored : null;
    return valid?.c[category] === true;
  };
  const googleIds = ["google-analytics", "google-ads", "google-tag-manager"];
  const analytics = on(googleIds, "analytics");
  const marketing = on(googleIds, "marketing");
  const functional = (stored && stored.rev === config.revision ? stored : null)?.c.functional === true;
  const state = (value: boolean) => (value ? "granted" : "denied") as "granted" | "denied";
  return {
    analytics_storage: state(analytics),
    ad_storage: state(marketing),
    ad_user_data: state(marketing),
    ad_personalization: state(marketing),
    functionality_storage: state(functional),
    personalization_storage: state(functional),
    security_storage: "granted",
  };
}

const LOADERS: Record<string, (value: string, config: ConsentClientConfig) => void> = {
  "google-analytics": (id) => {
    inject(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`);
    gtag("js", new Date());
    gtag("config", id);
  },
  "google-ads": (id) => {
    inject(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`);
    gtag("set", "ads_data_redaction", true);
    gtag("js", new Date());
    gtag("config", id);
  },
  "google-tag-manager": (id) => {
    const w = window as W;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    inject(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`);
  },
  "meta-pixel": (id) => {
    const w = window as W;
    if (!w.fbq) {
      const fbq = function (...args: unknown[]) {
        if (fbq.callMethod) fbq.callMethod(...args);
        else fbq.queue!.push(args);
      } as NonNullable<W["fbq"]>;
      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = "2.0";
      fbq.queue = [];
      w.fbq = fbq;
      w._fbq = fbq;
    }
    w.fbq("consent", "grant");
    w.fbq("init", id);
    w.fbq("track", "PageView");
    inject("https://connect.facebook.net/en_US/fbevents.js");
  },
  "linkedin-insight": (id) => {
    const w = window as W;
    w._linkedin_partner_id = id;
    w._linkedin_data_partner_ids = [...(w._linkedin_data_partner_ids || []), id];
    inject("https://snap.licdn.com/li.lms-analytics/insight.min.js");
  },
  "microsoft-uet": (id) => {
    const w = window as W;
    w.uetq = w.uetq || ([] as unknown as NonNullable<W["uetq"]>);
    w.uetq.push("consent", "update", { ad_storage: "granted" });
    inject("https://bat.bing.com/bat.js", {}, () => {
      if (!w.UET) return;
      const queue = new w.UET({ ti: id, enableAutoSpaTracking: true, q: w.uetq });
      w.uetq = queue as NonNullable<W["uetq"]>;
      w.uetq.push("pageLoad");
    });
  },
  "microsoft-clarity": (id) => {
    const w = window as W;
    if (!w.clarity) {
      const clarity = function (...args: unknown[]) { (clarity.q = clarity.q || []).push(args); } as NonNullable<W["clarity"]>;
      w.clarity = clarity;
    }
    inject(`https://www.clarity.ms/tag/${encodeURIComponent(id)}`);
    w.clarity("consent");
  },
  hotjar: (id) => {
    const w = window as W;
    if (!w.hj) {
      const hj = function (...args: unknown[]) { (hj.q = hj.q || []).push(args); } as NonNullable<W["hj"]>;
      w.hj = hj;
    }
    w._hjSettings = { hjid: Number(id), hjsv: 6 };
    inject(`https://static.hotjar.com/c/hotjar-${encodeURIComponent(id)}.js?sv=6`);
  },
};

/** Send the current choice to vendors that listen for it, then load every newly granted integration. */
export function applyIntegrations(config: ConsentClientConfig, stored: StoredConsent | null): void {
  if (config.integrations.some((integration) => integration.preset.startsWith("google-"))) gtag("consent", "update", googleConsentState(config, stored));
  for (const integration of config.integrations) {
    const key = `${integration.preset}:${integration.value}`;
    if (loaded.has(key)) continue;
    // Advanced Consent Mode (opt-in, GTM only): load the container with denied defaults; tags follow Consent Mode.
    const advanced = config.googleConsentAdvanced && integration.preset === "google-tag-manager";
    if (!advanced && !isGranted(integration.serviceId, config, stored)) continue;
    const loader = LOADERS[integration.preset];
    if (!loader) continue;
    loaded.add(key);
    try {
      loader(integration.value, config);
    } catch (error) {
      console.error(`[consent] ${integration.preset} failed to load`, error);
    }
  }
  const w = window as W;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event: "cms_consent_update", cms_consent: stored?.c || {}, cms_consent_services: stored?.s || {} });
}

/** Tell running vendors the visitor withdrew (AP: third parties must be informed). */
export function revokeIntegrations(config: ConsentClientConfig, revoked: string[]): void {
  const w = window as W;
  if (config.integrations.some((integration) => integration.preset.startsWith("google-"))) {
    gtag("consent", "update", { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
  }
  if (revoked.includes("meta-pixel")) w.fbq?.("consent", "revoke");
  if (revoked.includes("microsoft-uet")) w.uetq?.push("consent", "update", { ad_storage: "denied" });
  if (revoked.includes("microsoft-clarity")) w.clarity?.("consent", false);
}

/** Page views after client-side navigation (the App Router does not reload the page). */
export function trackPageView(config: ConsentClientConfig, stored: StoredConsent | null): void {
  const w = window as W;
  for (const integration of config.integrations) {
    if (!isGranted(integration.serviceId, config, stored)) continue;
    if (integration.preset === "google-analytics") gtag("event", "page_view", { page_location: location.href, page_title: document.title, send_to: integration.value });
    if (integration.preset === "meta-pixel") w.fbq?.("track", "PageView");
  }
}

export function vendorLoaded(preset: string): boolean {
  return [...loaded].some((key) => key.startsWith(`${preset}:`));
}
